<script lang="ts">
  import ProgressStepsContent from "@kontextwerk/web-sdk/ui/navigation/ProgressStepsContent.svelte"
  import { onMount } from "svelte"
  import ProgressSteps, {
    type ProgressStep,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import type { ProgressStepContext } from "@kontextwerk/web-sdk/core/navigation/progressSteps"
  import {
    firstCurrentWorkflowStep,
    LOCKED_WORKFLOW_STEP_MESSAGE,
    selectedWorkflowStepIndex,
    workflowStateForStep,
  } from "../../../../../../../../_shared/_logic/workflow-progress"
  import type {
    BlockerCreateInit,
    BlockerCreateOutputs,
    BlockerCreateStepId,
    BlockerCreateWorkflowEvent,
    OverlayHostProps,
  } from "./_shared/_logic/create-blocker-contract"
  import { applyBlockerCreateWorkflowEvent, nextBlockerCreateStep } from "./_logic/transitions"
  import GeneralStep from "./steps/a_general"
  import ConfigurationStep from "./steps/b_configuration"
  import DetailsStep from "./steps/c_details"

  type Props = {
    init?: BlockerCreateInit | null
    organizationId: string
    employees: BlockerCreateInit["employees"]
    scheduleEvents?: ScheduleEvent[]
    workingHoursByUser?: BlockerCreateInit["workingHoursByUser"]
    defaultEmployeeId?: string | null
    defaultStart?: Date | null
    defaultEnd?: Date | null
    onCreated?: (events: ScheduleEvent[]) => Promise<void> | void
  } & OverlayHostProps

  let {
    init: providedInit = null,
    organizationId,
    employees = [],
    scheduleEvents = [],
    workingHoursByUser = {},
    defaultEmployeeId = null,
    defaultStart = null,
    defaultEnd = null,
    onCreated = async () => {},
    onremoveOverlay = () => {},
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props = $props()

  const workflowInit: BlockerCreateInit = $derived(
    providedInit ?? {
      organizationId,
      employees,
      scheduleEvents,
      workingHoursByUser,
      defaultEmployeeId,
      defaultStart,
      defaultEnd,
    },
  )

  const stepOrder = ["general", "configuration", "details"] as const

  let selectedStep = $state<BlockerCreateStepId>("general")
  let outputs = $state<BlockerCreateOutputs>({})
  let stepError = $state<string | null>(null)

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(null)

    return () => setActionButtonSnippet?.(null)
  })

  const setStep = (step: BlockerCreateStepId) => {
    stepError = null
    selectedStep = step
  }

  const setStepError = (message: string | null) => {
    stepError = message
  }

  async function applyWorkflowEvent(event: BlockerCreateWorkflowEvent) {
    outputs = applyBlockerCreateWorkflowEvent(outputs, event)
    setStep(nextBlockerCreateStep(currentStep ?? selectedStep, event))
  }

  const completedSteps = $derived.by(() => {
    const completed: BlockerCreateStepId[] = []

    if (outputs.general) completed.push("general")

    if (outputs.configuration) completed.push("configuration")

    return completed
  })
  const currentStep = $derived(
    firstCurrentWorkflowStep<BlockerCreateStepId>({
      stepOrder,
      completedSteps,
      selectedStep,
    }),
  )
  const selectedIndex = $derived(
    selectedWorkflowStepIndex<BlockerCreateStepId>({
      stepOrder,
      completedSteps,
      selectedStep,
    }),
  )
  const handleSelectedIndexChange = (index: number) => {
    const next = stepOrder[index]

    if (next) setStep(next)
  }

  const progressSteps: ProgressStep[] = $derived([
    {
      id: "general",
      label: "Allgemein",
      workflowState: workflowStateForStep({ step: "general", currentStep, completedSteps }),
      snippet: generalStep,
    },
    {
      id: "configuration",
      label: "Zeitraum",
      workflowState: workflowStateForStep({ step: "configuration", currentStep, completedSteps }),
      snippet: configurationStep,
    },
    {
      id: "details",
      label: "Details",
      workflowState: workflowStateForStep({ step: "details", currentStep, completedSteps }),
      snippet: detailsStep,
    },
  ])
</script>

{#snippet generalStep(ctx: ProgressStepContext)}
  <GeneralStep
    init={workflowInit}
    output={outputs.general}
    readOnly={ctx.readOnly}
    onEvent={applyWorkflowEvent}
    onError={setStepError}
  />
{/snippet}

{#snippet configurationStep(ctx: ProgressStepContext)}
  {#if outputs.general}
    <ConfigurationStep
      init={workflowInit}
      general={outputs.general}
      output={outputs.configuration}
      readOnly={ctx.readOnly}
      onBack={() => setStep("general")}
      onEvent={applyWorkflowEvent}
      onError={setStepError}
    />
  {/if}
{/snippet}

{#snippet detailsStep(ctx: ProgressStepContext)}
  {#if outputs.general && outputs.configuration}
    <DetailsStep
      init={workflowInit}
      general={outputs.general}
      configuration={outputs.configuration}
      output={outputs.details}
      readOnly={ctx.readOnly}
      onBack={() => setStep("configuration")}
      {onCreated}
      onClose={onremoveOverlay}
      onError={setStepError}
    />
  {/if}
{/snippet}

<div class="create-blocker-overlay form light">
  <ProgressSteps
    steps={progressSteps}
    showControls={false}
    darkMode={true}
    syncUrl={false}
    statusMode="explicit"
    navigationMode="guarded"
    lockedStepMessage={LOCKED_WORKFLOW_STEP_MESSAGE}
    {selectedIndex}
    onSelectedIndexChange={handleSelectedIndexChange}
  />
  {#if stepError}
    <ProgressStepsContent>
      <div class="step-error">{stepError}</div>
    </ProgressStepsContent>
  {/if}
</div>

<style>
  .create-blocker-overlay {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .step-error {
    border: 1px solid var(--error-100);
    color: var(--error-100);
    padding: 0.75rem;
  }
</style>
